import {
  OrderMeal,
  OrderMealEntree,
  OrderMealItem,
  BasicOrderMealItem,
  PreConfiguredMeal,
  RecentOrFavoriteOrderMeal,
  OrderMealRequestMeal,
  MenuMetadataForChannel,
  PreConfiguredMealEntreeContent,
  OrderMealEntreeContents,
  MenuItemLookupResult,
  Menu,
  NonFoodItem,
} from '@/models';
import reduce from 'lodash/reduce';

export interface AnalyticsProduct {
  item_name?: string;
  item_id: string;
  price?: number;
  item_category: string;
  quantity?: number;
  /** filling, but I'm told GTM can't handle names */
  item_category2?: string;

  /** toppings, but I'm told GTM can't handle names */
  item_category3?: string;
}

export function mapMealItemToAnalyticsProduct(
  item: BasicOrderMealItem,
  category: 'entree' | 'side' | 'drink',
  activeMenu: Menu | null,
  price?: number,
  menuItemLookupMap?: Map<string, MenuItemLookupResult>,
  lifestyleMeals?: PreConfiguredMeal[]
): AnalyticsProduct {
  let name: string = item.menuItemName;
  if (category == 'entree' && activeMenu && activeMenu.entrees) {
    const foundMenuItem = activeMenu.entrees.find(x => x.itemId == item.menuItemId);
    if (foundMenuItem) {
      name = foundMenuItem.itemType;
    }
    if (lifestyleMeals) {
      const itemAsOrderMealItem = <OrderMealItem>item;
      if (itemAsOrderMealItem && itemAsOrderMealItem.preconfiguredItemName) {
        const isLifestyleMeal = (lifestyleMeals && lifestyleMeals.some(p => p.entree.itemId === item.menuItemId && p.mealName == itemAsOrderMealItem.preconfiguredItemName));
        if (isLifestyleMeal) {
          name = itemAsOrderMealItem.preconfiguredItemName;
        }
      }
    }
  }
  const product: AnalyticsProduct = {
    item_name: name,
    item_id: item.menuItemId,
    quantity: item.quantity,
    item_category: category,
  };

  if (price) {
    product.price = price;
  }
  if (category == 'entree' && menuItemLookupMap) {
    product.item_category2 = getFillingByEntreeItemId(item.menuItemId, menuItemLookupMap);
  }

  return product;
}

export function mapNonFoodItemToAnalyticsProduct(
  item: NonFoodItem,
  category: 'nonFoodItem',
  price?: number,
): AnalyticsProduct {

  const product: AnalyticsProduct = {
    item_name: item.menuItemName,
    item_id: item.menuItemId,
    quantity: item.quantity,
    item_category: category,
  };

  if (price) {
    product.price = price;
  }

  return product;
}

export function convertMealsToAnalyticsProducts(
  meals: OrderMeal[],
  metadata: MenuMetadataForChannel,
  menuItemLookupMap: Map<string, MenuItemLookupResult>,
  activeMenu: Menu | null,
  lifestyleMeals?: PreConfiguredMeal[]
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meals) {
    meals.forEach(meal => {
      if (meal.entrees) {
        meal.entrees.forEach((entree: OrderMealEntree) => {

          const product = mapMealItemToAnalyticsProduct(entree, 'entree', activeMenu, entree.entreeTotalAmount, menuItemLookupMap, lifestyleMeals);

          const toppings = generateOrderMealToppingsList(entree.contents, metadata);
          if (toppings) {
            product.item_category3 = toppings;
          }
          products.push(product);
        });
      }

      if (meal.sides) {
        meal.sides.forEach((side: OrderMealItem) => {
          const product = mapMealItemToAnalyticsProduct(side, 'side', activeMenu, side.itemTotalAmount);
          products.push(product);
        });
      }

      if (meal.drinks) {
        meal.drinks.forEach((drink: OrderMealItem) => {
          const product = mapMealItemToAnalyticsProduct(drink, 'drink', activeMenu, drink.itemTotalAmount);
          products.push(product);
        });
      }
    });
  }

  return products;
}

export function convertNonFoodItemsToAnalyticsProducts(
  nonFoodItems: NonFoodItem[]
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (nonFoodItems) {
    nonFoodItems.forEach(nonFoodItem => {
      const product = mapNonFoodItemToAnalyticsProduct(nonFoodItem, 'nonFoodItem', nonFoodItem.itemUnitPrice);
      products.push(product);
    });
  }

  return products;
}

export function convertPreconfiguredMealToAnalyticsProducts(
  meal: PreConfiguredMeal,
  metadata: MenuMetadataForChannel,
  menuItemLookupMap: Map<string, MenuItemLookupResult>,
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meal.entree) {
    const toppings = generatePreconfiguredToppingsList(meal.entree.contents, metadata);
    const product: AnalyticsProduct = {
      item_id: meal.entree.itemId,
      item_name: meal.entree.entreeName,
      price: meal.entree.unitPrice,
      item_category: 'entree',
      quantity: meal.entree.quantity,
      item_category2: getFillingByEntreeItemId(meal.entree.itemId, menuItemLookupMap),
    };
    if (toppings) {
      product.item_category3 = toppings;
    }
    products.push();
  }

  if (meal.sides) {
    meal.sides.forEach(side => products.push({ item_id: side.itemId, item_category: 'side' }));
  }

  if (meal.drinks) {
    meal.drinks.forEach(drink => products.push({ item_id: drink.itemId, item_category: 'drink' }));
  }

  return products;
}

export function convertMealRequestOrRecentOrFavoriteMealToAnalyticsProducts(
  meal: RecentOrFavoriteOrderMeal | OrderMealRequestMeal,
  activeMenu: Menu | null,
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meal.entrees) {
    meal.entrees.forEach((entree: BasicOrderMealItem) => {
      const product = mapMealItemToAnalyticsProduct(entree, 'entree', activeMenu);
      // TODO Generate toppings list from these two meal types
      products.push(product);
    });
  }

  if (meal.sides) {
    meal.sides.forEach((side: BasicOrderMealItem) => products.push(mapMealItemToAnalyticsProduct(side, 'side', activeMenu)));
  }

  if (meal.drinks) {
    meal.drinks.forEach((drink: BasicOrderMealItem) => products.push(mapMealItemToAnalyticsProduct(drink, 'drink', activeMenu)));
  }

  return products;
}

// Returns in the format of 'Steak Burrito, Carnitas Tacos, Large Chips & Guac'
export function reduceProductsToString(products: AnalyticsProduct[]): string {
  return reduce(
    products,
    (mealsOrdered, product) =>
      product.item_name ? `${mealsOrdered ? `${mealsOrdered}, ` : ''}${product.item_category == 'entree' ? `${product.item_category2} ${product.item_name}` : product.item_name}` : mealsOrdered,
    '',
  );
}

interface StringMap {
  [key: string]: string;
}
function generatePreconfiguredToppingsList(contents: PreConfiguredMealEntreeContent[], metadata: MenuMetadataForChannel): string | undefined {
  if (!metadata.itemSections.Toppings) {
    return;
  }

  const baseObj: StringMap = {};
  // e.g. {CMG-123: Queso, CMG-456: Salsa, ...}
  const idNameToppingMap = reduce(
    contents,
    (accu, content) => {
      return { ...accu, [content.itemId]: content.itemName };
    },
    baseObj,
  );
  const toppingIdsInEntree = Object.keys(idNameToppingMap);

  return reduce(
    metadata.itemSections.Toppings.items,
    (toppings: string[], item) => {
      if (toppingIdsInEntree.includes(item.menuItemId)) {
        toppings.push(idNameToppingMap[item.menuItemId]);
      }
      return toppings;
    },
    [],
  ).join(', ');
}

function getFillingByEntreeItemId(itemId: string, menuItemLookupMap: Map<string, MenuItemLookupResult>): string | undefined {
  const menuItem = menuItemLookupMap.get(itemId);
  if (menuItem) {
    return menuItem.primaryFillingName;
  }
}

function generateOrderMealToppingsList(contents: OrderMealEntreeContents[] | null, metadata: MenuMetadataForChannel): string | undefined {
  if (!metadata.itemSections.Toppings || !contents) {
    return;
  }

  const baseObj: StringMap = {};
  // e.g. {CMG-123: Queso, CMG-456: Salsa, ...}
  const idNameToppingMap = reduce(
    contents,
    (accu, content) => {
      return { ...accu, [content.menuItemId]: content.menuItemName };
    },
    baseObj,
  );
  const toppingIdsInEntree = Object.keys(idNameToppingMap);

  // Call everything but the protein a topping for an entree if available. It should always be available, but if not, fall back to subset of toppings (excluding rice & beans)
  const toppingMeta = metadata.itemSections.OptionContentGroup ? metadata.itemSections.OptionContentGroup.items : metadata.itemSections.Toppings.items;
  return reduce(
    toppingMeta,
    (toppings: string[], item) => {
      if (toppingIdsInEntree.includes(item.menuItemId)) {
        toppings.push(idNameToppingMap[item.menuItemId]);
      }
      return toppings;
    },
    [],
  ).join(', ');
}
